import { useEffect, useState, useRef } from 'react'
import React from 'react'
import { Link, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { child, get, getDatabase, ref } from 'firebase/database'
import fbConf from '../../FirebaseConf'
import Icon from '../../Icon'
import LoaderDots from '../../components/LoaderDots'
import { setPageTitle } from '../../utils/pageTitle'
import VoltieUser from '../../types/VoltieUser'
import { normalizeUser, fullName } from './userHelpers'
import { TabMenuContainer, TabMenuElement, TabContent, IconButton } from './userStyles'
import useScrollSelectedIntoView from '../../utils/useScrollSelectedIntoView'
import UserGeneralInformation from './UserGeneralInformation'
import UserDevices from './UserDevices'
import UserChargers from './UserChargers'
import UserNotifications from './UserNotifications'

const MENU_IDS = ['generalInformation', 'devices', 'chargers', 'settings'] as const

export default function User() {
  const { t, i18n } = useTranslation()
  const { uid, subpath } = useParams()
  const { isAdmin } = useOutletContext<{ isAdmin: boolean }>()
  const navigate = useNavigate()
  const location = useLocation()

  const [user, setUser] = useState<VoltieUser | null>()
  const [page, setPage] = useState<string>(MENU_IDS[0])

  async function fetchUser() {
    if (!uid) return
    try {
      const snap = await get(child(ref(getDatabase(fbConf)), `voltie/chargerusers/${uid}`))
      const val = snap.val()
      setUser(val ? normalizeUser(val) : null)
    } catch (e) {
      console.log(e)
      setUser(null)
    }
  }

  useEffect(() => {
    fetchUser()
  }, [uid])

  useEffect(() => {
    if (subpath) {
      if (!MENU_IDS.includes(subpath as typeof MENU_IDS[number])) {
        navigate('/404')
      } else {
        setPage(subpath)
      }
    } else {
      setPage(MENU_IDS[0])
    }
  }, [location])

  // Tab title: "<user name> - <tab>" (mirrors Charger.tsx)
  useEffect(() => {
    if (user) setPageTitle(`${fullName(user)} - ${t(`user_${page}`)}`)
  }, [user, page, i18n.language])

  const tabProps = { user: user as VoltieUser, uid: uid || '', isAdmin, refresh: fetchUser }

  const tabStripRef = useRef<HTMLDivElement>(null)
  const selectedTabRef = useRef<HTMLParagraphElement | null>(null)
  useScrollSelectedIntoView(tabStripRef, selectedTabRef, page)

  return (
    <React.Fragment key={uid}>
      <TabMenuContainer ref={tabStripRef}>
        <IconButton aria-label={t('menu_users')} onClick={() => navigate('/users')} style={{ marginRight: '4px' }}>
          <Icon path="general.backArrow" width="16px" height="16px" />
        </IconButton>
        <div role="tablist" aria-label={t('user_userDetails')} style={{ display: 'flex' }}>
          {MENU_IDS.map(id => (
            <Link key={id} role="tab" aria-selected={page === id} to={`/users/${uid}${id === 'generalInformation' ? '' : `/${id}`}`} style={{ textDecoration: 'none' }}>
              <TabMenuElement ref={el => { if (page === id) selectedTabRef.current = el }} $selected={page === id}>{t(`user_${id}`)}</TabMenuElement>
            </Link>
          ))}
        </div>
      </TabMenuContainer>

      <TabContent role="tabpanel" aria-label={t(`user_${page}`)}>
        {user === undefined && <div style={{ textAlign: 'center', marginTop: '80px' }}><LoaderDots /></div>}
        {user === null && <p style={{ textAlign: 'center', marginTop: '80px', fontWeight: 500 }}>{t('user_notFound')}</p>}
        {user && (
          <>
            {page === 'generalInformation' && <UserGeneralInformation {...tabProps} />}
            {page === 'devices' && <UserDevices {...tabProps} />}
            {page === 'chargers' && <UserChargers {...tabProps} />}
            {page === 'settings' && <UserNotifications {...tabProps} />}
          </>
        )}
      </TabContent>
    </React.Fragment>
  )
}
